import { useState, type KeyboardEvent, type ReactElement, type ReactNode, type RefObject } from 'react'
import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete'
import type { BaseUIEvent } from '@base-ui/react/types'
import { cn } from '@/lib/utils'
import { FieldShell, type FieldLabelProps, type FieldMessageProps, type FieldSize, type LabelAccessoryProps } from './Field'
import './autocomplete.css'

export type AutocompleteProps<Item> = Omit<FieldMessageProps, 'validate' | 'validationMode'> & FieldLabelProps & LabelAccessoryProps & {
  /** What is typed. */
  value: string
  onValueChange: (value: string) => void
  /** The suggestions for what is typed, already matched and in order. Empty while nothing is typed. */
  items: readonly Item[]
  /** An item as plain text: what fills the field when it is chosen, unless `keepValue`. */
  itemLabel: (item: Item) => string
  /** A stable key for each item. Defaults to its label. */
  itemKey?: (item: Item) => string
  /** An item's content in the list, such as a photo, a name and a line under it. Defaults to its label. */
  renderItem?: (item: Item) => ReactNode
  /** Items shown but not chosen, such as a person who isn't on the map. */
  itemDisabled?: (item: Item) => boolean
  /** Called when an item is chosen with a press or Enter. */
  onItemChoose?: (item: Item) => void
  /**
   * Called as the highlight moves (the arrow keys, the pointer, the first suggestion as the list opens), with the
   * highlighted item, or null once nothing is (the list closed or emptied): for a preview of the item.
   */
  onItemHighlight?: (item: Item | null) => void
  /** What is typed stays when an item is chosen: for a search whose items act (open, go to) rather than complete it. */
  keepValue?: boolean
  /** Said in the list when something is typed and nothing matches. */
  empty?: ReactNode
  /** A line at the foot of the list while it has items, announced as it changes, such as how many match. */
  status?: ReactNode
  /** Shown while the field is empty. */
  placeholder?: string
  /** A Lucide icon at the start of the field, such as Search. Decorative. */
  icon?: ReactElement
  /** Compact: the field looks 36px, like a compact TextField. The list's options keep 44px either way. */
  size?: FieldSize
  /** What the list opens under and matches in width. Defaults to the field. */
  anchor?: RefObject<Element | null>
  autoFocus?: boolean
  /** Runs before the field's own keys: `event.preventBaseUIHandler()` stops those, such as Escape clearing the field. */
  onKeyDown?: (event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>) => void
  name?: string
  disabled?: boolean
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  className?: string
}

/**
 * A text field that suggests as people type: the list opens below it, the first suggestion is highlighted, arrow keys
 * move through it and Enter or a press chooses one. Matching is the caller's: `items` are the suggestions for `value`.
 */
export function Autocomplete<Item>({
  label, hideLabel, labelAccessory, description, optional, error, requiredMessage, value, onValueChange, items, itemLabel, itemKey = itemLabel,
  renderItem, itemDisabled, onItemChoose, onItemHighlight, keepValue = false, empty, status, placeholder, icon, size = 'default', anchor, autoFocus, onKeyDown, name,
  disabled, container, className,
}: AutocompleteProps<Item>) {
  const [open, setOpen] = useState(false)
  const typed = value.trim() !== ''
  const input = (
    <BaseAutocomplete.Input
      className={cn('play-field', icon && 'play-field--with-icon', size === 'compact' && 'play-field--compact')}
      placeholder={placeholder}
      autoFocus={autoFocus}
      enterKeyHint="search"
      onKeyDown={onKeyDown}
    />
  )
  return (
    <FieldShell {...{ label, hideLabel, labelAccessory, description, optional, error, requiredMessage, name, disabled, className }}>
      <BaseAutocomplete.Root
        items={items}
        filter={null}
        itemToStringValue={itemLabel}
        value={value}
        onValueChange={(next, details) => {
          if (keepValue && details.reason === 'item-press') return
          onValueChange(next)
        }}
        // Nothing to show while nothing is typed: the list stays shut rather than open empty.
        open={open && typed}
        onOpenChange={setOpen}
        autoHighlight
        onItemHighlighted={onItemHighlight ? (item) => onItemHighlight((item as Item | undefined) ?? null) : undefined}
        disabled={disabled}
      >
        {icon ? (
          <span className={cn('play-field-adornment', size === 'compact' && 'play-field-adornment--compact')}>
            {input}
            <span className="play-field__icon" aria-hidden="true">{icon}</span>
          </span>
        ) : input}
        <BaseAutocomplete.Portal container={container ?? undefined}>
          <BaseAutocomplete.Positioner className="play-autocomplete__positioner" anchor={anchor} sideOffset={6} collisionPadding={12}>
            <BaseAutocomplete.Popup className="play-theme play-autocomplete__popup">
              <BaseAutocomplete.Empty className="play-autocomplete__content">{empty}</BaseAutocomplete.Empty>
              <BaseAutocomplete.List className="play-autocomplete__list">
                {(item: Item) => (
                  <BaseAutocomplete.Item
                    key={itemKey(item)}
                    value={item}
                    disabled={itemDisabled?.(item)}
                    className="play-autocomplete__item"
                    onClick={() => onItemChoose?.(item)}
                  >
                    {renderItem ? renderItem(item) : itemLabel(item)}
                  </BaseAutocomplete.Item>
                )}
              </BaseAutocomplete.List>
              <BaseAutocomplete.Status className="play-autocomplete__content play-autocomplete__status">{items.length > 0 ? status : null}</BaseAutocomplete.Status>
            </BaseAutocomplete.Popup>
          </BaseAutocomplete.Positioner>
        </BaseAutocomplete.Portal>
      </BaseAutocomplete.Root>
    </FieldShell>
  )
}
